import React from 'react';
import { useTranslation } from 'react-i18next';

import Grid from '@mui/material/Grid';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';

import Iconify from '../../../theme/iconify/iconify';

import Prompt from './Prompt';
import Model from '../../chat/Model';
import Message from '../../chat/Message';
import { mislecAPI } from '../../../util/axios';

function Chat({ user, updateUser, course, section, findOutMorePrompt }) {
    const [chat, setChat] = React.useState(user.courses.filter(c => c.id === course)[0].chat);
    const [model, setModel] = React.useState(user.courses.filter(c => c.id === course)[0].chat?.model || 'Qwen');
    const [prompt, setPrompt] = React.useState('');
    const [typing, setTyping] = React.useState(false);

    React.useEffect(() => {
        if (!findOutMorePrompt) return;

        const data = {
            model: model,
            course: course,
            section: section,
            messages: [...chat.messages, {
                role: 'USER',
                text: findOutMorePrompt.user_prompt
            }],
        };
        setChat(data);
        setPrompt('');
        setTyping(true);

        //  Send data to server
        mislecAPI.post(`/api/chat/find_out_more`, findOutMorePrompt)
            .then(res => {
                setTyping(false);

                //  Display response
                setChat(res.data.chat);

                //  Update user
                mislecAPI.get(`/api/users/${user._id}`)
                    .then(res => updateUser(res.data))
                    .catch(error => {
                        //  Expired session
                        if (error.response)
                            if (error.response.data.detail === 'api.auth.verify.401')
                                updateUser(null);
                    });
            })
            .catch(error => {
                setTyping(false);

                //  Expired session
                if (error.response)
                    if (error.response.data.detail === 'api.auth.verify.401')
                        updateUser(null);
            });
    }, [findOutMorePrompt]);

    const { t } = useTranslation();
    const chatContainerRef = React.useRef(null);

    React.useEffect(() => {
        if (chatContainerRef.current)
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }, [chat]);

    const sendMessage = () => {
        if (!user) return;
        if (prompt === '') return;


        const data = {
            model: model,
            course: course,
            section: section,
            messages: [...chat.messages, {
                role: 'USER',
                text: prompt
            }],
        };
        setChat(data);
        setPrompt('');
        setTyping(true);

        mislecAPI.put(`/api/course/${course}/chat`, data)
            .then(res => {
                setTyping(false);

                //  Display response
                setChat(res.data.chat);

                //  Update user
                mislecAPI.get(`/api/users/${user._id}`)
                    .then(res => updateUser(res.data))
                    .catch(error => {
                        //  Expired session
                        if (error.response)
                            if (error.response.data.detail === 'api.auth.verify.401')
                                updateUser(null);
                    });
            })
            .catch(error => {
                setTyping(false);

                //  Expired session
                if (error.response)
                    if (error.response.data.detail === 'api.auth.verify.401')
                        updateUser(null);
            });
    }

    return (
        <>
            <Container
                sx={{
                    display: 'flex',
                    flex: 1,
                    flexDirection: 'column',
                    justifyContent: 'flex-end',
                    height: '100%',
                    mb: 0,
                    mt: 10
                }}
            >
                {chat && chat.messages.length > 0 ?
                    <Container ref={chatContainerRef} sx={{ mb: { xs: 0, md: 2 }, px: 0, overflowY: 'auto', scrollbarWidth: 'none' }}>
                        {chat &&
                            chat.messages.map((message, index) => (
                                <Message key={index} message={message} />
                            ))
                        }
                    </Container>
                    :
                    <Container
                        sx={{
                            display: 'flex',
                            flex: 1,
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            px: 0
                        }}>
                        <Grid item xs={12} sx={{ textAlign: 'center', width: { xs: '80%', md: '50%' } }}>
                            <Typography variant='h3'>{t('chat.new.title')}</Typography>
                        </Grid>
                        <Grid item xs={12} sx={{ textAlign: 'center', width: { xs: '80%', md: '50%' } }}>
                            <Typography variant='body1'>
                                {t('course.chat.start')} <b>{t('course.chat.mISLec')}</b>{t('course.chat.suffix')} {t('course.chat.end')}
                            </Typography>
                        </Grid>
                    </Container>
                }
                <Container sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'sticky',
                    bottom: 0,
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: '8px',
                    px: 0,
                    background: 'transparent'
                }}>
                    {typing && (
                        <>
                            <Grid
                                container
                                alignItems='center'
                                justifyContent='flex-start'
                                sx={{
                                    fontSize: '0.875rem',
                                    mb: 0.5,
                                    ml: 1
                                }}
                            >
                                {t('chat.message.typing')}
                                <Iconify
                                    icon='svg-spinners:3-dots-fade'
                                    style={{
                                        display: 'flex',
                                        alignSelf: 'flex-end',
                                        fontSize: '12px',
                                        height: '55%',
                                        width: 'auto',
                                        marginLeft: 1,
                                        marginBottom: 1,
                                    }}
                                />
                            </Grid>
                        </>
                    )}
                    <Prompt
                        prompt={prompt}
                        setPrompt={setPrompt}
                        sendMessage={sendMessage}
                        model={<Model updateUser={updateUser} updateModel={(model) => setModel(model)} selectedModel={model} />}
                    />
                </Container>
            </Container>
        </>
    );
}

export default Chat;
